import React, { useContext } from 'react';
import { Badge, Box, Button, Link, MenuItem, Select, Stack, Typography } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { GridCellParams } from '@mui/x-data-grid-pro';

import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { UsersContext } from '../context';
import { ActionMenu, QuickSearch } from 'components/_atoms';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import { User } from 'views/App/Portal/LexID/Users/_types/user';
import { SORT_OPTIONS, STATUS_OPTIONS } from '../context';
import PageToolbar from '_lex/_atoms/PageToolbar';

const UsersList: React.FC = () => {
  const { currentOrganizationId, hasPermission } = useContext(AppContext);
  const { users, usersQuery, params, updateParams } = useContext(UsersContext);

  const columns: ColumnDef = {
    name: {
      header: t('users.properties.name'),
      flex: 2,
      renderer: (params: GridCellParams) => {
        const user = params.row as User;
        const name = [user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ');
        return (
          <Link href={`./${user.id}`}>
            <Typography
              variant='h6'
              sx={{ fontWeight: 'semibold' }}
            >
              {name || '**MISSING**'}
            </Typography>
          </Link>
        );
      },
    },
    email: {
      header: t('users.properties.email'),
      flex: 2,
      renderer: (params: GridCellParams) => {
        const user = params.row as User;
        return user.email ? <Link href={`mailto:${user.email}`}>{user.email}</Link> : null;
      },
    },
    active: {
      header: t('users.properties.active'),
      flex: 1,
      renderer: (params: GridCellParams) => {
        const user = params.row as User;
        return (
          <Badge
            variant='inline'
            badgeContent={user.active ? 'Active' : 'Inactive'}
            color={user.active ? 'success' : 'error'}
            sx={{ textTransform: 'uppercase' }}
          />
        );
      },
    },
    ...(currentOrganizationId && hasPermission('updateUser', currentOrganizationId)
      ? {
        actions: {
          header: '',
          flex: 0.25,
          renderer: (params: GridCellParams) => {
            const user = params.row as User;
            return (
              <ActionMenu
                id={`action_menu_${user.id}`}
                items={[
                  {
                    key: `edit_${user.id}`,
                    label: t('common.edit'),
                    href: `./edit/${user.id}`,
                  },
                ]}
              />
            );
          },
        },
      }
      : {}),
  };

  const actions = (
    <Stack
      direction='row'
      spacing={4}
    >
      <Select
        id='status'
        label={t('common.status')}
        value={params.status || 'active'}
        size='small'
        onChange={({ target }) => updateParams({ status: target.value })}
      >
        {STATUS_OPTIONS.map((x) => (
          <MenuItem
            key={`status_${x.key}`}
            value={x.key}
          >{`${t('common.status')}: ${t(x.label)}`}</MenuItem>
        ))}
      </Select>
      <Select
        id='sort'
        label={t('common.sort')}
        value={params.sort}
        size='small'
        onChange={({ target }) => updateParams({ sort: target.value })}
      >
        {SORT_OPTIONS.map((x) => (
          <MenuItem
            key={`sort_${x.key}`}
            value={x.key}
          >{`${t('common.sort')}: ${t(x.label)}`}</MenuItem>
        ))}
      </Select>
    </Stack>
  );

  const createButton =
    currentOrganizationId && hasPermission('createUser', currentOrganizationId) ? (
      <Button
        variant='contained'
        size='small'
        href='./new'
      >
        {t('users.actions.create')}
      </Button>
    ) : null;

  return (
    <CommonLayout
      headerProps={{
        title: t('users.title'),
        actions: createButton,
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <QuickSearch
              id='user_keyword_search'
              initialValue={params.search || ''}
              onChange={(val) => updateParams({ search: val })}
              placeholder={t('users.search.placeholder')}
            />
          }
          rightItems={actions}
        />
        <GridSection
          columns={columns}
          data={users || []}
          query={usersQuery}
          gridProps={{
            getRowId: (row: User) => uuidv4(),
          }}
        />
      </Box>
    </CommonLayout>
  );
};

export default UsersList;
